import {useTranslation} from 'react-i18next';
import {useEffect, useMemo} from 'react';
import {Stack} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {Modal} from '../../../../../components/modal/modal';
import FlatInput from '../../../../../components/flat-input/flat-input';
import {AffiliatedUser} from '../../../../../backend/responses/affiliated-user';
import {translations} from '../../../../../i18n/translations';
import {EntityOccurrenceTypeCategory} from '../../../../../backend/responses/entity-occurrence';
import {FlatSelectDropdown} from '../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {FlatStartsAtDate} from '../../../../../components/flat-starts-at-date/flat-starts-at-date';
import {FlatStartsAtTime} from '../../../../../components/flat-starts-at-time/flat-starts-at-time';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../../utils/constants/forms';
import {RESPONSIBLE_NOT_DEFINED} from '../professional-caregivers/shifts/actions-shift-flow/actions-shift-form';
import {useEntity} from '../../../../../contexts/entity/context';
import {AddAttachmentsForm} from '../../../../../components/daily-care-log/attachments/add-attachments-form';
import {sortOccurrenceTypesByName} from '../../../../../utils/data/occurrences';
import {AddOccurrenceFormData} from '../../../daily-care-log/occurrences/add-occurrences-form';

type EditOccurrenceModalProps = {
  users?: AffiliatedUser[];
  professionals?: AffiliatedUser[];
  submitting: boolean;
  type: EntityOccurrenceTypeCategory;
  onClose: () => void;
  onAdd: (values: AddOccurrenceFormData) => void;
};

export default function AddOccurrenceModal({
  users,
  professionals,
  submitting,
  type,
  onClose,
  onAdd,
}: EditOccurrenceModalProps) {
  const {t} = useTranslation();
  const {
    state: {occurrencesTypes},
  } = useEntity();
  const {
    control,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    formState: {errors},
    getValues,
    register,
  } = useForm<AddOccurrenceFormData>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const [occurrenceType, occurrenceDate, occurrenceTime, occurrencePatient, occurrenceDetails] =
    watch([
      'occurrenceType',
      'occurrenceDate',
      'occurrenceTime',
      'occurrencePatient',
      'occurrenceDetails',
    ]);

  const occurrenceTypeOptions = useMemo(
    () =>
      sortOccurrenceTypesByName(
        occurrencesTypes?.filter(
          (occurrenceType) =>
            occurrenceType.occurrenceType.category === type && occurrenceType.summary.isActive
        ) || []
      ).map((occurrenceType) => {
        return {
          key: occurrenceType.occurrenceType.id,
          label: occurrenceType.occurrenceType.name,
          category: occurrenceType.occurrenceType.category,
        };
      }),
    [occurrencesTypes, type]
  );

  useEffect(() => {
    const defaultValues: AddOccurrenceFormData = {
      occurrenceType: '',
      occurrenceDate: new Date(),
      occurrenceTime: new Date(),
      occurrenceLocal: '',
      occurrencePatient: '',
      occurrenceResponsible: '',
      occurrenceDetails: '',
    };
    reset(defaultValues);
  }, [reset]);

  const isValid =
    (type === 'patients' &&
      occurrencePatient &&
      occurrenceType &&
      occurrenceDetails &&
      occurrenceDate &&
      occurrenceTime) ||
    (type === 'general' && occurrenceType && occurrenceDetails && occurrenceDate && occurrenceTime)
      ? true
      : false;

  return (
    <Modal
      open
      maxWidth="sm"
      fullWidth
      preventCloseClick
      title={`${t(translations.office.professional_caregiver.occurrences.add_occurrence)} - ${t(
        translations.office.professional_caregiver.settings.occurrences[type]
      )}`}
      onClose={onClose}
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
          disabled: submitting,
        },
        {
          label: t(translations.modal.add),
          type: 'nondestructive',
          onClick: () => onAdd(getValues()),
          disabled: submitting || Object.keys(errors).length > 0 || !isValid,
          loading: submitting,
        },
      ]}
    >
      <form>
        <Stack display="flex" flexDirection="column" gap="22px">
          <Controller
            name="occurrenceType"
            control={control}
            rules={{required: true}}
            render={({field: {value}}) => {
              const selected = occurrencesTypes?.find((type) => type.occurrenceType.id === value);
              return (
                <FlatSelectDropdown
                  label={t(translations.daily_care_log.occurrences.type)}
                  required
                  loading={!occurrencesTypes}
                  options={occurrenceTypeOptions}
                  value={
                    selected && value
                      ? {
                          key: value,
                          label: selected?.occurrenceType.name || '',
                        }
                      : undefined
                  }
                  onChange={(value) => setValue('occurrenceType', value.key as string)}
                  placeholder={t(
                    translations.office.professional_caregiver.occurrences.type_placeholder
                  )}
                />
              );
            }}
          />

          <Stack display="flex" flexDirection="row" gap="32px">
            <Controller
              name="occurrenceDate"
              control={control}
              rules={{required: true}}
              render={({field: {value}}) => {
                return (
                  <FlatStartsAtDate
                    value={value}
                    required
                    disablePast={false}
                    label={t(translations.daily_care_log.occurrences.date)}
                    onChange={(value) => {
                      if (value) setValue('occurrenceDate', value);
                    }}
                  />
                );
              }}
            />

            <Controller
              name="occurrenceTime"
              control={control}
              rules={{required: true}}
              render={({field: {value}}) => {
                return (
                  <FlatStartsAtTime
                    value={value}
                    required
                    label={t(translations.daily_care_log.occurrences.time)}
                    onChange={(value) => {
                      if (value) setValue('occurrenceTime', value);
                    }}
                  />
                );
              }}
            />
          </Stack>
          <Controller
            name="occurrenceLocal"
            control={control}
            render={() => {
              return (
                <FlatInput
                  {...register('occurrenceLocal', {
                    maxLength: {
                      value: 35,
                      message: t(translations.daily_care_log.occurrences.local_error, {
                        characters: 35,
                      }),
                    },
                  })}
                  label={t(translations.daily_care_log.occurrences.local)}
                  errors={errors.occurrenceLocal?.message}
                  placeholder={t(
                    translations.office.professional_caregiver.occurrences.local_placeholder
                  )}
                />
              );
            }}
          />
          {type === 'patients' && (
            <Controller
              name="occurrencePatient"
              control={control}
              rules={{required: type === 'patients'}}
              render={({field: {value}}) => {
                const selected = users?.find((user) => user.user.id === value);
                return (
                  <FlatSelectDropdown
                    label={t(translations.daily_care_log.occurrences.patient)}
                    required
                    loading={!users}
                    role="user"
                    options={
                      users
                        ?.filter((user) => user.user.type === 'senior' && user.user.isActive)
                        .map((user) => {
                          return {
                            key: user.user.id || '',
                            label: user.user.name,
                            picture: user.user.picture,
                            borderColor: user.entitySocialResponses[0]
                              ? user.entitySocialResponses[0].foregroundColor
                              : '',
                          };
                        }) || []
                    }
                    value={
                      value && selected
                        ? {
                            key: value,
                            label: selected.user.name,
                            picture: selected.user.picture,
                            borderColor: selected.entitySocialResponses[0]
                              ? selected.entitySocialResponses[0].foregroundColor
                              : '',
                          }
                        : undefined
                    }
                    onChange={(value) => setValue('occurrencePatient', value.key as string)}
                    placeholder={t(
                      translations.office.professional_caregiver.occurrences.patient_placeholder
                    )}
                  />
                );
              }}
            />
          )}
          <Controller
            name="occurrenceResponsible"
            control={control}
            render={({field: {value}}) => {
              const selected = professionals?.find((user) => user.user.id === value);
              return (
                <FlatSelectDropdown
                  label={t(translations.daily_care_log.occurrences.responsible)}
                  loading={!users}
                  role="user"
                  options={
                    !professionals
                      ? []
                      : [
                          {
                            key: RESPONSIBLE_NOT_DEFINED,
                            label: t(
                              translations.shifts_page.shift_modal.shift_form
                                .shift_responsible_placeholder
                            ),
                          },
                          ...professionals
                            .filter((user) => user.user.type === 'caregiver' && user.user.isActive)
                            ?.map((professional) => {
                              return {
                                key: professional.user.id || '',
                                label: professional.user.name,
                                picture: professional.user.picture,
                              };
                            }),
                        ]
                  }
                  value={
                    value && selected
                      ? {key: value, label: selected?.user.name, picture: selected.user.picture}
                      : value && !selected
                        ? {
                            key: value,
                            label: t(
                              translations.shifts_page.shift_modal.shift_form
                                .shift_responsible_placeholder
                            ),
                          }
                        : undefined
                  }
                  onChange={(value) => setValue('occurrenceResponsible', value.key as string)}
                  placeholder={t(
                    translations.office.professional_caregiver.occurrences.responsible_placeholder
                  )}
                />
              );
            }}
          />
          <Controller
            name="occurrenceDetails"
            control={control}
            render={({field: {value}}) => {
              return (
                <FlatInput
                  required
                  multiline
                  rows={3}
                  label={t(translations.daily_care_log.occurrences.details)}
                  value={value}
                  placeholder={t(
                    translations.office.professional_caregiver.occurrences.details_placeholder
                  )}
                  onChange={(event) => setValue('occurrenceDetails', event.target.value)}
                />
              );
            }}
          />
          <Controller
            name="occurrenceFiles"
            control={control}
            render={({field: {value}}) => {
              return (
                <AddAttachmentsForm
                  type="upload"
                  attachments={value}
                  onAttachmentsChange={(value) => setValue('occurrenceFiles', value)}
                  onError={(errors) => {
                    if (errors.length > 0) {
                      setError('occurrenceFiles', {message: 'Error'});
                    } else {
                      clearErrors('occurrenceFiles');
                    }
                  }}
                />
              );
            }}
          />
        </Stack>
      </form>
    </Modal>
  );
}
